import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp, TrendingDown, Wallet, AlertTriangle, Receipt, FileText,
  Plus, Users, ArrowUpRight, ArrowDownRight, Minus, Clock, ArrowRight,
  DollarSign,
} from 'lucide-react';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement,
  ArcElement, Title, Tooltip, Legend, Filler
);

const MND = ['Jan','Feb','Mar','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Des'];

// ── Hjelpere ─────────────────────────────────────────────────────────────────
const beregnTrend = (na, fjor) => {
  if (!fjor) return null;
  const diff = na - fjor;
  const pct = (diff / Math.abs(fjor)) * 100;
  return { diff, pct };
};

const TrendBadge = ({ trend, positivErBra = true }) => {
  if (!trend || !isFinite(trend.pct)) return null;
  const opp = trend.diff > 0;
  const bra = positivErBra ? opp : !opp;
  const Icon = trend.diff === 0 ? Minus : (opp ? ArrowUpRight : ArrowDownRight);
  const farge = trend.diff === 0 ? 'var(--theme-text-muted)'
              : (bra ? 'var(--theme-success)' : 'var(--theme-danger)');
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 11,
                   fontWeight: 600, color: farge }}>
      <Icon size={12} />
      {Math.abs(trend.pct).toFixed(1)}%
    </span>
  );
};

// ── KPI-kort med trend ───────────────────────────────────────────────────────
const KpiKort = ({ label, verdi, ikon: Ikon, farge, trend, positivErBra = true, lenke }) => {
  const innhold = (
    <div className="card" style={{
      padding: 16, position: 'relative',
      borderLeft: `3px solid ${farge}`,
      cursor: lenke ? 'pointer' : 'default',
      transition: 'transform 0.15s, box-shadow 0.15s',
    }}
    onMouseEnter={(e) => { if (lenke) e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={(e) => { if (lenke) e.currentTarget.style.transform = 'translateY(0)'; }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
        <span className="fm-text-muted" style={{ fontSize: 11, textTransform: 'uppercase',
                                                 letterSpacing: '0.05em', fontWeight: 600 }}>
          {label}
        </span>
        {Ikon && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 6,
                        background: `${farge}22`, color: farge }}>
            <Ikon size={14} />
          </div>
        )}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--theme-text-primary)',
                    lineHeight: 1.1 }}>
        {verdi}
      </div>
      {trend && (
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <TrendBadge trend={trend} positivErBra={positivErBra} />
          <span className="fm-text-muted" style={{ fontSize: 10 }}>vs. samme periode i fjor</span>
        </div>
      )}
    </div>
  );
  return lenke ? <Link to={lenke} style={{ textDecoration: 'none' }}>{innhold}</Link> : innhold;
};

// ── Hurtighandling-knapp ─────────────────────────────────────────────────────
const HurtigKnapp = ({ til, ikon: Ikon, label, farge }) => (
  <Link to={til} style={{ textDecoration: 'none', flex: '1 1 160px', minWidth: 140 }}>
    <div className="card" style={{
      padding: 14, display: 'flex', alignItems: 'center', gap: 10,
      cursor: 'pointer', transition: 'transform 0.15s, background 0.15s',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--theme-bg-hover)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--theme-bg-card)'; }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 36, height: 36, borderRadius: 8,
                    background: `${farge}22`, color: farge }}>
        <Ikon size={18} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-text-primary)' }}>
          {label}
        </div>
      </div>
      <ArrowRight size={14} className="fm-text-muted" />
    </div>
  </Link>
);

// ── Hovedkomponent ───────────────────────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/dashboard').then((r) => setData(r.data));
  }, []);

  if (!data) return <span className="fm-text-muted">Laster…</span>;

  const trendInntekt = beregnTrend(data.hia_inntekter, data.fjor_inntekter);
  const trendUtgift  = beregnTrend(data.hia_utgifter, data.fjor_utgifter);
  const trendResultat = beregnTrend(
    data.hia_inntekter - data.hia_utgifter,
    data.fjor_inntekter - data.fjor_utgifter
  );

  // Hilsen
  const time = new Date().getHours();
  const hils = time < 5 ? 'God natt' : time < 11 ? 'God morgen'
             : time < 17 ? 'God dag' : time < 22 ? 'God kveld' : 'God natt';

  // Resolve CSS-variabler til faktiske farger (Chart.js forstår ikke var(--...))
  const css = getComputedStyle(document.documentElement);
  const colTextSec = css.getPropertyValue('--theme-text-secondary').trim() || '#9ca3af';
  const colTextMut = css.getPropertyValue('--theme-text-muted').trim() || '#6b7280';

  // Bar-chart data (måned for måned)
  const barData = {
    labels: MND,
    datasets: [
      {
        label: 'Inntekter', data: data.chart_inntekt,
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderRadius: 4, borderSkipped: false,
      },
      {
        label: 'Utgifter', data: data.chart_utgift,
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderRadius: 4, borderSkipped: false,
      },
    ],
  };
  const barOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom',
                labels: { color: colTextSec, boxWidth: 12, padding: 14, font: { size: 11 } } },
      tooltip: {
        callbacks: { label: (c) => `${c.dataset.label}: ${kr(c.raw)}` },
      },
    },
    scales: {
      x: { ticks: { color: colTextMut, font: { size: 10 } },
           grid: { display: false } },
      y: { ticks: { color: colTextMut, font: { size: 10 },
                    callback: (v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
           grid: { color: 'rgba(128,128,128,0.1)' }, beginAtZero: true },
    },
  };

  // Donut: utgift pr kategori
  const FARGER = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const donutData = {
    labels: data.utgift_kategorier.map((k) => k.kategori),
    datasets: [{
      data: data.utgift_kategorier.map((k) => k.sum),
      backgroundColor: FARGER,
      borderWidth: 2,
      borderColor: 'var(--theme-bg-card)',
    }],
  };
  const donutOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: {
      legend: { position: 'right',
                labels: { color: colTextSec, boxWidth: 10, padding: 8, font: { size: 11 } } },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${kr(c.raw)}` } },
    },
  };

  return (
    <>
      {/* Hilsen-header */}
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--theme-text-primary)' }}>
          {hils}{user?.navn ? `, ${user.navn.split(' ')[0]}` : ''}
        </h1>
        <p className="fm-text-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Her er oversikten din for {data.aar}.
        </p>
      </div>

      {/* Hurtighandlinger */}
      <div className="flex" style={{ gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <HurtigKnapp til="/faktura/ny"  ikon={FileText} label="Ny faktura"  farge="#3b82f6" />
        <HurtigKnapp til="/bilag"       ikon={Receipt}  label="Nytt bilag"  farge="#10b981" />
        <HurtigKnapp til="/tilbud/ny"   ikon={Plus}     label="Nytt tilbud" farge="#f59e0b" />
        <HurtigKnapp til="/kontakter"   ikon={Users}    label="Ny kontakt"  farge="#8b5cf6" />
      </div>

      {/* KPI-kort */}
      <div className="grid" style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 18,
      }}>
        <KpiKort label="Inntekter (eks. MVA)" verdi={kr(data.inntekter)}
                 ikon={TrendingUp} farge="#10b981" trend={trendInntekt} />
        <KpiKort label="Utgifter (eks. MVA)" verdi={kr(data.utgifter)}
                 ikon={TrendingDown} farge="#ef4444" trend={trendUtgift} positivErBra={false} />
        <KpiKort label="Resultat hittil i år" verdi={kr(data.resultat)}
                 ikon={Wallet} farge="#3b82f6" trend={trendResultat} />
        <KpiKort label="Utestående fakturaer"
                 verdi={kr(data.ubetalte_sum)}
                 ikon={DollarSign} farge="#f59e0b"
                 lenke="/faktura" />
      </div>

      {/* Forfalte fakturaer (vises kun hvis det finnes) */}
      {data.forfalte_count > 0 && (
        <Card style={{
          marginBottom: 18, padding: 14,
          borderLeft: '3px solid var(--theme-danger)',
          background: 'color-mix(in srgb, var(--theme-danger) 8%, var(--theme-bg-card))',
        }}>
          <div className="flex items-center justify-between" style={{ marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <AlertTriangle size={16} style={{ color: 'var(--theme-danger)' }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {data.forfalte_count} forfalt{data.forfalte_count === 1 ? '' : 'e'} faktura{data.forfalte_count === 1 ? '' : 'er'} – {kr(data.forfalte_sum)}
              </span>
            </div>
            <Link to="/faktura" className="btn-ghost" style={{ fontSize: 11 }}>
              Se alle <ArrowRight size={12} />
            </Link>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {data.forfalte_fakturaer.map((f) => (
              <div key={f.id}
                   onClick={() => nav(`/faktura/${f.id}`)}
                   className="flex items-center justify-between"
                   style={{
                     padding: '6px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                     background: 'var(--theme-bg-card)',
                   }}
                   onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--theme-bg-hover)'; }}
                   onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--theme-bg-card)'; }}>
                <div className="flex items-center" style={{ gap: 10 }}>
                  <Clock size={12} style={{ color: 'var(--theme-danger)' }} />
                  <span style={{ fontWeight: 600 }}>{f.fakturanummer}</span>
                  <span className="fm-text-muted">{f.kunde_navn}</span>
                </div>
                <div className="flex items-center" style={{ gap: 12 }}>
                  <span className="fm-text-muted">Forfall: {dato(f.forfall)}</span>
                  <span style={{ fontWeight: 600 }}>{kr(f.sum_ink_mva)}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Hovedchart + donut */}
      <div className="grid" style={{
        gridTemplateColumns: '2fr 1fr', gap: 12, marginBottom: 18,
      }}>
        <Card>
          <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>Måned for måned ({data.aar})</h3>
            <span className="fm-text-muted" style={{ fontSize: 11 }}>Inntekter vs. utgifter</span>
          </div>
          <div style={{ height: 280 }}>
            <Bar data={barData} options={barOpts} />
          </div>
        </Card>

        <Card>
          <h3 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600 }}>Utgifter pr. kategori</h3>
          {data.utgift_kategorier.length === 0 ? (
            <p className="fm-text-muted" style={{ fontSize: 12 }}>Ingen utgifter registrert enda.</p>
          ) : (
            <div style={{ height: 280 }}>
              <Doughnut data={donutData} options={donutOpts} />
            </div>
          )}
        </Card>
      </div>

      {/* Lister */}
      <div className="grid" style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12,
      }}>
        <Card>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <Receipt size={14} /> Siste bilag
            </h3>
            <Link to="/bilag" className="fm-text-muted" style={{ fontSize: 11, textDecoration: 'none' }}>
              Se alle <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
            </Link>
          </div>
          {data.siste_bilag.length === 0 ? (
            <p className="fm-text-muted" style={{ fontSize: 12 }}>Ingen bilag enda.</p>
          ) : (
            <table>
              <thead><tr>
                <th>Dato</th><th>Type</th><th>Beskrivelse</th>
                <th style={{ textAlign: 'right' }}>Beløp</th>
              </tr></thead>
              <tbody>
                {data.siste_bilag.map((b) => (
                  <tr key={b.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{dato(b.dato)}</td>
                    <td>
                      <span className={`badge ${b.type === 'inntekt' ? 'badge-success' : 'badge-danger'}`}>
                        {b.type}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.beskrivelse}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{kr(b.belop_eks_mva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card>
          <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 13, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} /> Siste fakturaer
            </h3>
            <Link to="/faktura" className="fm-text-muted" style={{ fontSize: 11, textDecoration: 'none' }}>
              Se alle <ArrowRight size={11} style={{ verticalAlign: 'middle' }} />
            </Link>
          </div>
          {data.siste_fakturaer.length === 0 ? (
            <p className="fm-text-muted" style={{ fontSize: 12 }}>Ingen fakturaer enda.</p>
          ) : (
            <table>
              <thead><tr>
                <th>#</th><th>Kunde</th><th>Status</th>
                <th style={{ textAlign: 'right' }}>Sum</th>
              </tr></thead>
              <tbody>
                {data.siste_fakturaer.map((f) => (
                  <tr key={f.id} onClick={() => nav(`/faktura/${f.id}`)} style={{ cursor: 'pointer' }}>
                    <td style={{ fontWeight: 600 }}>{f.fakturanummer}</td>
                    <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.kunde_navn}
                    </td>
                    <td><StatusBadge status={f.status} /></td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{kr(f.sum_ink_mva)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
};

const StatusBadge = ({ status }) => {
  const map = { betalt: 'badge-success', sendt: 'badge-purple', usendt: 'badge-neutral', forfalt: 'badge-danger' };
  return <span className={`badge ${map[status] || 'badge-neutral'}`}>{status}</span>;
};

export default Dashboard;
